import { useMemo, useState } from 'react';

import {
  DifficultyLevel,
  IconButton,
  MultipleChoiceQuestion,
  MultipleChoiceQuestionAnswer,
  MultipleChoiceQuestionAnswerType,
  MultipleChoiceQuestionType,
} from '@opositandopn/ui';
import {
  FcAcceptDatabase,
  FcAddDatabase,
  FcDataBackup,
  FcDeleteDatabase,
} from 'react-icons/fc';
import { IoArrowForwardCircleOutline } from 'react-icons/io5';

import { Loading } from '../../../../components/core/Loading';
import { useIsPremium, useProfile } from '../../../../context/ProfileContext';
import { useAppActions } from '../../../../state/appStore';
import { BaseComponentProps } from '../../../../types/base-component.types';
import { ErrorReportForm } from '../../../reports/components/error-report-form/ErrorReportForm';
import {
  CreateMultipleChoiceQuestionAttemptCommandResult,
  MultipleChoiceQuestionViewModel,
  SubmitQuestionAttemptCommandResult,
  useBookmarkMultipleChoiceQuestion,
  useCreateMultipleChoiceQuestionErrorReport,
  useExplainMultipleChoiceQuestionAttempt,
  useSubmitMultipleChoiceQuestionAttempt,
  useUnbookmarkMultipleChoiceQuestion,
} from '../../api/useMultipleChoiceQuestions';

import styles from './MultipleChoiceQuestionAttempt.module.scss';

export type ContainerProps = BaseComponentProps & {
  countdown?: boolean;
  attempt?: CreateMultipleChoiceQuestionAttemptCommandResult;
  onNext: () => void;
  onClose: () => void;
};

function MultipleChoiceQuestionAttempt({
  countdown = true,
  attempt,
  onNext,
  onClose,
}: ContainerProps) {
  const { profile } = useProfile();
  const { setShowGetPremiumPopup } = useAppActions();
  const isPremium = useIsPremium();
  const [showFeedback, setShowFeedback] = useState(false);

  const [attemptResult, setAttemptResult] =
    useState<
      SubmitQuestionAttemptCommandResult<MultipleChoiceQuestionViewModel>
    >();

  const { mutate: submitMultipleChoiceQuestionAttempt, isPending } =
    useSubmitMultipleChoiceQuestionAttempt();

  const { mutateAsync: bookmarkMultipleChoiceQuestion } =
    useBookmarkMultipleChoiceQuestion();

  const { mutateAsync: unbookmarkMultipleChoiceQuestion } =
    useUnbookmarkMultipleChoiceQuestion();

  const { mutate: createMultipleChoiceQuestionErrorReport } =
    useCreateMultipleChoiceQuestionErrorReport();

  const { explain: explainMultipleChoiceQuestionAttempt } =
    useExplainMultipleChoiceQuestionAttempt();

  const attemptQuestion = useMemo<MultipleChoiceQuestionType | undefined>(
    () =>
      attempt && {
        id: attempt.id,
        text: attempt.text,
        subject: attempt.lesson?.name,
        options: attempt.choices.map((choice) => choice.text),
        selectedOptionIndex: 0,
        bookmarked: attempt.bookmarked ?? false,
        imageUrl: attempt.imageUrl,
      },
    [attempt],
  );

  const attemptResultQuestion = useMemo<
    MultipleChoiceQuestionAnswerType | undefined
  >(
    () =>
      attemptResult && {
        id: attemptResult.questionAttempt.question.id,
        text: attemptResult.questionAttempt.question.text,
        subject:
          attemptResult.questionAttempt.question.lesson?.name ??
          attemptResult.questionAttempt.question.unit?.name ??
          attemptResult.questionAttempt.question.subject.name,
        caption:
          attemptResult.questionAttempt.question.status === 'removed'
            ? '(Pregunta eliminada)'
            : undefined,
        imageUrl: attemptResult.questionAttempt.question.imageUrl,
        bookmarked: attemptResult.questionAttempt.question.bookmarked,
        tags: attemptResult.questionAttempt.question.tags.map(
          (tag) => tag.name,
        ),
        difficulty:
          attemptResult.questionAttempt.question.difficultyLevel === 'easy'
            ? DifficultyLevel.EASY
            : attemptResult.questionAttempt.question.difficultyLevel ===
                'medium'
              ? DifficultyLevel.MEDIUM
              : DifficultyLevel.HARD,
        options: attemptResult.questionAttempt.question.choices.map(
          (choice) => choice.text,
        ),
        selectedOptionIndex: attemptResult.questionAttempt.answer
          ? attemptResult.questionAttempt.question.choices.findIndex(
              (choice) => choice.id === attemptResult.questionAttempt.answer,
            ) + 1
          : 0,
        explanation: attemptResult.questionAttempt.correction.feedback,
        reason: attemptResult.questionAttempt.question.explanation,
        correctOptionIndex:
          attemptResult.questionAttempt.question.choices.findIndex(
            (choice) => choice.isCorrect,
          ) + 1,
        answerRate: attemptResult.questionAttempt.question.metrics && {
          correct:
            attemptResult.questionAttempt.question.metrics.correctPercentage,
          skipped:
            attemptResult.questionAttempt.question.metrics.skippedPercentage,
          incorrect:
            attemptResult.questionAttempt.question.metrics.incorrectPercentage,
        },
        auditLog:
          profile.role === 'admin'
            ? attemptResult.questionAttempt.question.auditLog.map((log) => ({
                icon:
                  log.action === 'created' ? (
                    <FcAddDatabase />
                  ) : log.action === 'updated' ? (
                    <FcDataBackup />
                  ) : log.action === 'deleted' ? (
                    <FcDeleteDatabase />
                  ) : (
                    <FcAcceptDatabase />
                  ),
                title:
                  log.action === 'created'
                    ? 'Pregunta creada'
                    : log.action === 'updated'
                      ? 'Pregunta actualizada'
                      : log.action === 'deleted'
                        ? 'Pregunta eliminada'
                        : 'Pregunta verificada',

                description: `Por ${log.username}`,
                date: new Date(log.date),
              }))
            : undefined,
        tip:
          attemptResult.questionAttempt.question.missesCount > 1 ? (
            <div className={styles['tooltip']}>
              Has fallado esta pregunta{' '}
              {attemptResult.questionAttempt.question.missesCount} veces
            </div>
          ) : undefined,
      },
    [attemptResult, profile.role],
  );

  return (
    <>
      {attemptResult && attemptResultQuestion ? (
        <>
          <MultipleChoiceQuestionAnswer
            className={styles['questionnaire']}
            question={attemptResultQuestion}
            onBookmark={
              profile.subscription?.isActive &&
              attemptResult.questionAttempt.question.status !== 'removed'
                ? async (id: string) => {
                    await bookmarkMultipleChoiceQuestion({
                      subjectId: attempt?.subject.id ?? '',
                      questionId: id,
                    });
                  }
                : undefined
            }
            onUnbookmark={
              profile.subscription?.isActive
                ? async (id: string) => {
                    await unbookmarkMultipleChoiceQuestion({
                      subjectId: attempt?.subject.id ?? '',
                      questionId: id,
                    });
                  }
                : undefined
            }
            onFeedback={
              isPremium &&
              attemptResult.questionAttempt.question.status !== 'removed'
                ? () => {
                    setShowFeedback(true);
                  }
                : undefined
            }
            onExplain={
              attemptResult.questionAttempt.question.status !== 'removed' &&
              !attemptResult.questionAttempt.question.imageUrl
                ? async () => {
                    if (!isPremium) {
                      setShowGetPremiumPopup(true);
                      throw new Error('Premium subscription required.');
                    }
                    const result = await explainMultipleChoiceQuestionAttempt({
                      subjectId:
                        attemptResult.questionAttempt.question.subject.id,
                      questionAttemptId: attemptResult.questionAttempt.id,
                    });
                    setAttemptResult((prev) =>
                      prev
                        ? {
                            ...prev,
                            questionAttempt: {
                              ...prev.questionAttempt,
                              correction: {
                                ...prev.questionAttempt.correction,
                                feedback: result.explanation,
                              },
                            },
                          }
                        : prev,
                    );
                  }
                : undefined
            }
            onClose={onClose}
          >
            <IconButton
              icon={<IoArrowForwardCircleOutline />}
              onClick={() => {
                onNext();
                setAttemptResult(undefined);
              }}
            />
          </MultipleChoiceQuestionAnswer>
          <ErrorReportForm
            open={showFeedback}
            title="¿Has visto algún error en esta pregunta?"
            onFeedback={async (feedback) => {
              createMultipleChoiceQuestionErrorReport(
                {
                  subjectId: attempt?.subject.id ?? '',
                  questionId: attemptResult.questionAttempt.question.id,
                  message: feedback,
                },
                {
                  onSuccess: () => {
                    setShowFeedback(false);
                  },
                },
              );
            }}
            onClose={() => setShowFeedback(false)}
          />
        </>
      ) : attempt && attemptQuestion ? (
        <MultipleChoiceQuestion
          className={styles['questionnaire']}
          question={attemptQuestion}
          timeInSeconds={countdown ? attempt.timeLimit : undefined}
          isSubmitting={isPending}
          onSubmit={(questionAnswer) => {
            const answer =
              questionAnswer.answerIndex === 0
                ? ''
                : attempt.choices[questionAnswer.answerIndex - 1].id;

            submitMultipleChoiceQuestionAttempt(
              {
                subjectId: attempt.subject.id,
                questionAttemptId: attempt.id,
                answer,
              },
              {
                onSuccess: (
                  data: SubmitQuestionAttemptCommandResult<MultipleChoiceQuestionViewModel>,
                ) => {
                  setAttemptResult(data);
                },
              },
            );
          }}
          onClose={onClose}
          onBookmark={
            profile.subscription?.isActive
              ? async () => {
                  await bookmarkMultipleChoiceQuestion({
                    subjectId: attempt.subject.id,
                    questionId: attempt.questionId,
                  });
                }
              : undefined
          }
          onUnbookmark={
            profile.subscription?.isActive
              ? async () => {
                  await unbookmarkMultipleChoiceQuestion({
                    subjectId: attempt.subject.id,
                    questionId: attempt.questionId,
                  });
                }
              : undefined
          }
        />
      ) : (
        <Loading />
      )}
    </>
  );
}

export { MultipleChoiceQuestionAttempt };
